import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { USER_TYPES } from '@medflex/utils/constants';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import Button from '@medflex/components/stories/Button';
import { IconDateAdd } from '@medflex/components/stories/Icons';
import isWebView from '@medflex/utils/isWebView';
import isAnySessionChatId from '@medflex/utils/isAnySessionChatId';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@medflex/utils/Tracking';
import isSelf from '@medflex/utils/isSelf';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import Badge, { KINDS } from '@medflex/components/stories/Badge';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import config from '@medflex/utils/config';
import isSessionPMChatId from '@medflex/utils/isSessionPMChatId';
import { STATUS } from '../store/models/subscription';
import useFeatures from '../hooks/useFeatures';
import useModal from '../hooks/useModal';
import useSessionCalls from '../hooks/useSessionCalls';
import useUserProfile from '../hooks/useUserProfile';
import useSessionChat from '../hooks/useSessionChat';
import useSubscription from '../hooks/useSubscription';
import useSelectedContact from '../hooks/useSelectedContact';
import { MODALS } from '../hooks/useIsModal';
import isSubContactId from '../../utils/isSubContactId';

const { SESSIONS_URL } = config;

const MAX_CALL_PARTICIPANTS = 10;

const VideoMenuModule = ({ id, canCall, className, children }) => {
  const { t } = useTranslation('messenger');
  const { open } = useModal();
  const { userProfile } = useUserProfile();
  const callWindow = useRef();
  const {
    create: createSessionCall,
    outgoing: outgoingSessionCall,
    getUrl: getCallUrl,
  } = useSessionCalls();
  const { chat } = useSessionChat({ id });
  const { selectedContact } = useSelectedContact();
  const isSession = id && isAnySessionChatId(id);
  const isPmChat = id && isSessionPMChatId(id);
  const { status: subscriptionStatus } = useSubscription();
  const [meetingFeature, sessionMeetingGroupFeature] = useFeatures([
    FEATURE_KEYS.SESSION_MEETING,
    FEATURE_KEYS.SESSION_MEETING_GROUP,
  ]);

  const outgoingSessionCallId = outgoingSessionCall && outgoingSessionCall.id;
  const userProfileType = userProfile && userProfile.type;
  const chatParticipantsLength = chat && chat.participants && chat.participants.length;
  const selectedContactUser = selectedContact && selectedContact.user;
  const isGroup = !isPmChat && isSession && chatParticipantsLength && chatParticipantsLength > 2;

  const isAllowedToScheduleMeeting = useMemo(() => {
    if (subscriptionStatus === STATUS.LOADED) {
      if (isPmChat && userProfileType !== USER_TYPES.PATIENT) return true;

      if (isSession) {
        return (
          userProfileType !== USER_TYPES.PATIENT && chatParticipantsLength <= MAX_CALL_PARTICIPANTS
        );
      }

      if (selectedContactUser) {
        return !(
          isSelf(selectedContactUser) ||
          isSupportPatient({ id: selectedContactUser.id }) ||
          isSubContactId(selectedContactUser.id)
        );
      }
    }

    return false;
  }, [
    subscriptionStatus,
    isPmChat,
    isSession,
    selectedContactUser,
    userProfileType,
    chatParticipantsLength,
  ]);

  useEffect(() => {
    if (outgoingSessionCallId) {
      callWindow.current.location = getCallUrl({ id: outgoingSessionCallId });
    }
  }, [outgoingSessionCallId]);

  const getSessionCallee = () =>
    chat ? chat.participants.find(participant => participant.userId !== userProfile.id) : null;

  const handleCall = event => {
    trackEvent(event, {
      action: 'ChatTitleBar | button',
      actionText: ' Jetzt per Video  | (audience)',
    });

    // This URL-Path is checked on the desktop client to open video calls in an extra window
    callWindow.current = window.open(`${SESSIONS_URL}/loading`, '_blank');

    const userId = isSession ? getSessionCallee().userId : id;
    createSessionCall([{ userId }, { userId: userProfile.id, roles: ['moderator'] }]);
  };

  const handleSchedule = event => {
    if (isGroup && !sessionMeetingGroupFeature.active) {
      open({ modal: 'featureUpgradeSessionMeetingGroup' });
      return;
    }

    if (!isGroup && !meetingFeature.active) {
      open({ modal: 'featureUpgradeSessionMeeting' });
      return;
    }

    trackEvent(event, {
      action: 'ChatTitleBar | button',
      actionText: 'Videotermin anlegen  | (audience)',
    });
    open({
      modal: MODALS.sessionsCreate,
      mode: isGroup ? 'group' : 'single',
      ...(!isSession ? { userId: id } : { sessionId: id }),
      inChat: true,
    });
  };

  const items = [];

  if (canCall && !isWebView()) {
    items.push(
      <PopupMenu.Item key="startCall" onClick={handleCall}>
        <VideoCameraIcon className="h-5 w-5" />
        <span className="ml-4">{t('VideoMenuModule.call_start')}</span>
      </PopupMenu.Item>,
    );
  }

  if (isAllowedToScheduleMeeting) {
    items.push(
      <PopupMenu.Item key="scheduleMeeting" onClick={handleSchedule}>
        <IconDateAdd size="5" />
        <span className="ml-4">
          {isGroup
            ? t('VideoMenuModule.schedule_session_meeting_group')
            : t('VideoMenuModule.schedule_session_meeting')}
          {((isGroup && !sessionMeetingGroupFeature.active) ||
            (!isGroup && !meetingFeature.active)) && (
            <Badge kind={KINDS.SIGNAL} className="ml-2">
              {t('VideoMenuModule.upgrade')}
            </Badge>
          )}
        </span>
      </PopupMenu.Item>,
    );
  }

  if (!items.length) return null;

  return (
    <PopupMenu>
      <PopupMenu.Menu>
        <PopupMenu.Group>{items}</PopupMenu.Group>
      </PopupMenu.Menu>
      <PopupMenu.Trigger>
        {({ triggerRef }) => children({ triggerRef, className })}
      </PopupMenu.Trigger>
    </PopupMenu>
  );
};

VideoMenuModule.defaultProps = {
  className: '',
  canCall: true,
  id: null,
  children: ({ triggerRef, className }) => (
    <Button
      innerRef={triggerRef}
      shape="circle"
      size="sm"
      kind="secondary:ghost"
      className={className}
    >
      <VideoCameraIcon className="w-7 h-7" />
    </Button>
  ),
};

VideoMenuModule.propTypes = {
  className: PropTypes.string,
  canCall: PropTypes.bool,
  id: PropTypes.string,
  children: PropTypes.func,
};

export default VideoMenuModule;
